import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import {
    defaultLanguage,
    isSupportedLanguage,
    supportedLanguages,
    toSupportedLanguage,
    type SupportedLanguage,
} from './language-config.ts';
import { resources } from './resources/resources.ts';

/**
 * localStorage key holding the user's manual language choice. Its presence is
 * what makes a choice manual and outranks the tenant default, so only
 * `applyManualLanguage` may write it — never detection.
 */
export const manualLanguageStorageKey = 'platoLanguage';

if (typeof window !== 'undefined') {
    // i18next auto-cached detection results into this legacy key. Stale values
    // would shadow the tenant default forever, so clear it.
    window.localStorage.removeItem('i18nextLng');
}

// Initialize i18next
void i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
        resources,
        supportedLngs: supportedLanguages,
        fallbackLng: defaultLanguage,
        defaultNS: 'common',
        load: 'languageOnly',
        detection: {
            // Manual choice first. The browser language is only the placeholder
            // until the tenant default arrives with the app config. `caches` is
            // empty so detection never masquerades as a manual choice.
            order: ['localStorage', 'navigator'],
            lookupLocalStorage: manualLanguageStorageKey,
            caches: [],
        },
        // debug: process.env.NODE_ENV === 'development',
        interpolation: {
            escapeValue: false, // not needed for react as it escapes by default
        },
        react: {
            useSuspense: false,
        },
    });

i18n.on('initialized', () => (document.documentElement.lang = i18n.language));
i18n.on('languageChanged', (lng) => (document.documentElement.lang = lng));

const hasManualLanguage = (): boolean => {
    const stored = localStorage.getItem(manualLanguageStorageKey);
    return stored !== null && isSupportedLanguage(stored);
};

/**
 * Applies the tenant default language (`AppConfig.defaultLanguage`) unless the
 * user has manually chosen a language. Call it whenever the app config
 * arrives, including refetches: a mid-session change of the tenant default
 * should reach every user without a manual choice.
 */
export function applyTenantDefaultLanguage(tenantDefault: SupportedLanguage): void {
    if (hasManualLanguage()) {
        return;
    }
    if (toSupportedLanguage(i18n.language) !== tenantDefault) {
        void i18n.changeLanguage(tenantDefault);
    }
}

/**
 * Records a deliberate language choice from the Settings picker and switches
 * to it. The stored choice outranks the tenant default on every future load,
 * on this browser, until the next manual choice.
 */
export function applyManualLanguage(language: SupportedLanguage): void {
    localStorage.setItem(manualLanguageStorageKey, language);
    void i18n.changeLanguage(language);
}

export { i18n };
